import {
  CloudUploadOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  EllipsisOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  FileZipOutlined,
  InfoCircleOutlined,
  LockOutlined,
  PrinterOutlined,
  SearchOutlined,
  SendOutlined,
  SplitCellsOutlined,
  WarningTwoTone,
} from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-components';
import { useMutation, useQuery } from '@apollo/client/react';
import { css } from '@emotion/css';
import { Markable } from '@medcom/shared-ui/components/Markable.tsx';
import { tabularNums } from '@medcom/shared-ui/styles/globalCss.ts';
import { filterByTokens } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { App, Badge, Button, Dropdown, Flex, Input, Modal, Popover, Space, Table, Tag, Tooltip, Typography, theme } from 'antd';
import type { ItemType } from 'antd/es/menu/interface';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import dayjs from 'dayjs';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router';
import { useDebounce } from 'use-debounce';
import { useLaunchOnly } from '../ais/useAisCapabilities';
import { MainContent } from '../components/MainContent';
import { RequestStatusBadge } from '../components/RequestStatusBadge';
import { TableColumnSettings } from '../components/TableColumnSettings';
import type { PatientDataInput } from '../graphql/generated/graphql.ts';
import { BillingType, type OpenRequestsQuery, OrderType, RequestStatus } from '../graphql/generated/graphql.ts';
import { useAppStore } from '../hooks/store/useAppStore';
import { useCurrentContextStore } from '../hooks/store/useCurrentContextStore.ts';
import type { TableColumnSetting } from '../hooks/store/useUserSettingsStore.ts';
import { useAisExport } from '../hooks/useAisExport';
import { useLocalExport } from '../hooks/useLocalExport.ts';
import { usePrintEmptyLabel } from '../hooks/usePrintEmptyLabel';
import { usePrintExtraLabels } from '../hooks/usePrintExtraLabels.ts';
import { useCanPrint } from '../hooks/usePrinter.ts';
import { usePrintRequest } from '../hooks/usePrintRequest';
import { useRequestCopy } from '../hooks/useRequestCopy';
import { useSendRequests } from '../hooks/useSendRequests';
import { useSingleFlight } from '../hooks/useSingleFlight';
import { useSplitRequest } from '../hooks/useSplitRequest.ts';
import { PrintButton } from '../PrintButton.tsx';
import type { UpdateEvent } from '../socket/types.ts';
import { useSubscription } from '../socket/useSubscription.ts';
import { tableActionCell, tableHoverPointer } from '../styles/globalCss';
import { SHORT_DATE_FORMAT, SHORT_DATETIME_FORMAT } from '../utils/dateFormatUtils';
import { patientFullName } from '../utils/patient.ts';
import { hasFrontDeskRole, hasSomeRole, Role } from '../utils/user';
import { FormsModal } from './forms/FormsModal.tsx';
import { DELETE_OPEN_REQUESTS_MUTATION, OPEN_REQUESTS_QUERY } from './OpenRequests.gql';
import { printMenuItem, usePatientMenuItems } from './patientMenuItems.tsx';
import { RequestDetailsModal } from './request/RequestDetailsModal';
import { showCopyWarnings } from './showCopyWarnings';

type Request = NonNullable<OpenRequestsQuery['openRequests']>[number];

const isDefineLater = (request: Request) =>
  request.orders.flatMap(it => it.orderParameters).some(it => it.billingType === BillingType.DEFINE_LATER);
const NO_PERMISSION_FOR_ALL_ORDERS = 'Sie haben keine Berechtigung für alle Aufträge dieser Anforderung.';

// never split in a launch-only session or an AIS-linked Anforderung: the parts would lose the AIS request
const offerSplit = (modal: ReturnType<typeof App.useApp>['modal'], noSplit: boolean, action: string, verb: string, onSplit: () => void) => {
  const title = `${action} nicht möglich - Multi-Labor-Anforderung`;
  if (noSplit) {
    modal.info({ width: 500, maskClosable: true, title, content: NO_PERMISSION_FOR_ALL_ORDERS });
    return;
  }
  modal.confirm({
    width: 500,
    maskClosable: true,
    title,
    content: `${NO_PERMISSION_FOR_ALL_ORDERS} Bitte teilen sie die Multi-Labor-Anforderung in Einzelanforderungen auf, wenn sie die Anforderung ${verb} wollen. Wollen Sie die Anforderung jetzt aufteilen?`,
    okText: 'Anforderung aufteilen',
    cancelText: 'Abbrechen',
    okButtonProps: { icon: <SplitCellsOutlined /> },
    onOk: onSplit,
  });
};

// menu entries offered only to some roles or sessions
const itemsIf = (show: boolean, ...items: ItemType[]): ItemType[] => (show ? items : []);

const isTruncated = (request: Request) => request.ordersTruncated;

export const OpenRequests: React.FC = () => {
  const auth = useAuth();
  const { currentDoctorId, primaryDoctorId } = useCurrentContextStore();
  const { notification, message, modal } = App.useApp();
  const { token } = theme.useToken();
  // SVNR / Auftragsnummer are identifiers → monospace (see .claude/rules/param-name-display.md).
  const mono = css`
    font-family: ${token.fontFamilyCode};
  `;
  const setRequestIdForUpdate = useAppStore(state => state.setRequestIdForUpdate);
  const setExecuteRequestCopy = useAppStore(state => state.setExecuteRequestCopy);
  const navigate = useNavigate();
  const print = usePrintRequest();
  const canPrint = useCanPrint();
  const launchOnly = useLaunchOnly();
  const aisExport = useAisExport();
  const printEmptyLabel = usePrintEmptyLabel();
  const printExtraLabels = usePrintExtraLabels();
  const send = useSendRequests();
  const split = useSplitRequest();
  const { generateAndExport } = useLocalExport();
  const [search, setSearch] = useState<string | null>(null);
  const [debouncedSearch] = useDebounce(search, 250);
  const [filteredRequests, setFilteredRequests] = useState<Request[]>([]);
  const [selectedRequests, setSelectedRequests] = useState<Request[]>([]);
  const [deleteRequest, setDeleteRequest] = useState<Request | null>(null);
  const [detailsRequestId, setDetailsRequestId] = useState<string | null>(null);
  const [formsPatientData, setFormsPatientData] = useState<PatientDataInput | null>(null);
  const patientMenuItems = usePatientMenuItems(setFormsPatientData);

  const deletedAllowed = hasSomeRole([Role.ROLE_LR_MEDCOM, Role.ROLE_LR_USER, Role.ROLE_LR_LAB_ADMIN, Role.ROLE_LR_FRONT_DESK], auth.user);
  const editAllowed = hasSomeRole([Role.ROLE_LR_MEDCOM, Role.ROLE_LR_USER, Role.ROLE_LR_LAB_ADMIN, Role.ROLE_LR_FRONT_DESK], auth.user);
  const localExportAllowed = hasSomeRole([Role.ROLE_LR_MEDCOM, Role.ROLE_LR_LAB_ADMIN], auth.user);
  const isFrontDesk = hasFrontDeskRole(auth.user);

  const { data, loading, refetch } = useQuery(OPEN_REQUESTS_QUERY, {
    variables: {
      currentDoctorId: currentDoctorId,
      doctorId: isFrontDesk ? primaryDoctorId : currentDoctorId,
    },
  });

  const callback = useCallback(
    (event: UpdateEvent) => {
      console.debug(`received UI update: ${JSON.stringify(event)}`);
      refetch();
    },
    [refetch]
  );

  useSubscription({ type: 'request-created', tenantId: currentDoctorId }, callback);
  useSubscription({ type: 'request-updated', tenantId: currentDoctorId }, callback);
  useSubscription({ type: 'request-comment-updated', tenantId: currentDoctorId }, callback);
  useSubscription({ type: 'request-deleted', tenantId: currentDoctorId }, callback);
  useSubscription({ type: 'request-printed', tenantId: currentDoctorId }, callback);
  useSubscription({ type: 'request-sent', tenantId: currentDoctorId }, callback);

  useRequestCopy('update', true, null, copyData => {
    showCopyWarnings(notification, copyData, !!data?.doctor?.flipParamNames);
    navigate('/anforderung/parameterauswahl');
  });

  const printWithFeedback = async (request: Request) => {
    const labelsPrinted = await print(
      'before-send',
      request,
      () => message.error('Beim Drucken ist ein Fehler aufgetreten'),
      () =>
        message.info(
          <>
            Es sind bereits alle erforderlichen Etiketten und Dokumente für diese Anforderung gedruckt worden. Zum erneuten Drucken öffnen
            Sie bitte die{' '}
            <Button type="link" onClick={() => setDetailsRequestId(request.id)}>
              Anforderungsdetails
            </Button>
          </>
        ),
      () => message.success('Etiketten & Dokumente wurden an den Drucker gesendet'),
      () => message.error('Anforderungsstatus kann nicht gesetzt werden'),
      () => refetch()
    );

    if (labelsPrinted) {
      printExtraLabels(data?.doctor, request.patientData, request);
    }
  };

  const [printOnce, printAvailable] = useSingleFlight(printWithFeedback, true);

  const [deleteOpenRequestsMutation] = useMutation(DELETE_OPEN_REQUESTS_MUTATION);

  const [columnSettings, setColumnSettings] = useState<TableColumnSetting[]>([
    { id: 'status', visible: true, title: 'Status', checkable: false },
    { id: 'patientData.lastName', visible: true, title: 'Nachname', checkable: true },
    { id: 'patientData.firstName', visible: true, title: 'Vorname', checkable: true },
    { id: 'createdAt', visible: true, title: 'Erstellt am', checkable: false },
    { id: 'patientData.svnr', visible: true, title: 'SVNR', checkable: true },
    { id: 'patientData.birthday', visible: true, title: 'Geburtstag', checkable: true },
    { id: 'sampledAt', visible: false, title: 'Probenentnahme am', checkable: true },
    { id: 'orders', visible: true, title: 'Auftrag', checkable: true },
    { id: 'creator', visible: false, title: 'Erstellt von', checkable: true },
    { id: 'modifier', visible: false, title: 'Bearbeitet von', checkable: true },
    { id: 'modifiedAt', visible: false, title: 'Bearbeitet am', checkable: true },
    { id: 'comment', visible: false, title: 'Kommentar', checkable: true },
  ]);

  const onEdit = (id: string) => {
    setExecuteRequestCopy(true);
    setRequestIdForUpdate(id);
  };

  const onSplit = async (id: string) => {
    try {
      await split(id);
      message.success('Die Anforderung wurde erfolgreich aufgeteilt');
    } catch (_e) {
      message.error('Die Anforderung konnte nicht aufgeteilt werden');
    }
  };

  const columns: ColumnsType<Request> = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      ellipsis: true,
      sorter: (a, b) => a.status.localeCompare(b.status),
      // fallow-ignore-next-line complexity
      render: (value, record) => {
        const defineLater = isDefineLater(record);
        const truncated = isTruncated(record);
        return (
          <Flex gap="middle">
            <div
              className={css`
                min-width: 150px;
              `}
            >
              {!defineLater && <RequestStatusBadge status={value} />}
              {defineLater && (
                <Tooltip title="Es wurden noch nicht alle benötigen Verrechnungsinformationen angegeben. Die Anforderung kann dadurch nicht gesendet werden. Bitte bearbeiten Sie die Anforderung und ergänzen Sie die Verrechnungsinformationen.">
                  <Tag color="error" icon={<ExclamationCircleOutlined />}>
                    Aktion erforderlich
                  </Tag>
                </Tooltip>
              )}
            </div>
            {record.status === RequestStatus.NEW && (
              <PrintButton
                size="small"
                type="link"
                className={css`
                    padding: 0;
                  `}
                icon={<PrinterOutlined />}
                disabled={!printAvailable}
                onClick={e => {
                  e.stopPropagation();
                  printOnce(record);
                }}
              >
                Drucken
              </PrintButton>
            )}
            {record.status === RequestStatus.PRINTED && !truncated && !defineLater && (
              <Flex gap="small">
                <Button
                  size="small"
                  type="link"
                  className={css`
                    padding: 0;
                  `}
                  icon={<SendOutlined />}
                  disabled={defineLater}
                  onClick={e => {
                    e.stopPropagation();
                    sendRequest(record.id, false);
                  }}
                >
                  Senden
                </Button>
                {localExportAllowed && (
                  <Button
                    size="small"
                    type="link"
                    className={css`
                      padding: 0;
                    `}
                    icon={<FileZipOutlined />}
                    disabled={defineLater || !window.nativeApi}
                    onClick={e => {
                      e.stopPropagation();
                      sendRequest(record.id, true);
                    }}
                  >
                    Lokal erzeugen
                  </Button>
                )}
              </Flex>
            )}
          </Flex>
        );
      },
    },
    {
      title: 'Nachname',
      dataIndex: ['patientData', 'lastName'],
      key: 'patientData.lastName',
      sorter: (a, b) => a.patientData.lastName.localeCompare(b.patientData.lastName),
      width: 110,
      ellipsis: true,
      render: (_, record) => (
        <Typography.Text style={{ fontWeight: 500 }}>
          <Markable tokens={debouncedSearch ?? ''}>{record.patientData.lastName}</Markable>
        </Typography.Text>
      ),
    },
    {
      title: 'Vorname',
      dataIndex: ['patientData', 'firstName'],
      key: 'patientData.firstName',
      sorter: (a, b) => a.patientData.firstName.localeCompare(b.patientData.firstName),
      width: 100,
      ellipsis: true,
      render: (_, record) => <Markable tokens={debouncedSearch ?? ''}>{record.patientData.firstName}</Markable>,
    },
    {
      title: 'Erstellt am',
      dataIndex: 'createdAt',
      key: 'createdAt',
      defaultSortOrder: 'descend',
      width: 100,
      ellipsis: true,
      sorter: (a, b) => a.createdAt.localeCompare(b.createdAt),
      render: value => <span className={tabularNums}>{dayjs(value).format(SHORT_DATETIME_FORMAT)}</span>,
    },
    {
      title: 'SVNR',
      dataIndex: ['patientData', 'svnr'],
      key: 'patientData.svnr',
      width: 70,
      ellipsis: true,
      sorter: (a, b) => a.patientData.svnr.localeCompare(b.patientData.svnr),
      render: value => (
        <span className={mono}>
          <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>
        </span>
      ),
    },
    {
      title: 'Geburtstag',
      dataIndex: ['patientData', 'birthday'],
      key: 'patientData.birthday',
      width: 120,
      ellipsis: true,
      sorter: (a, b) => a.patientData.birthday.localeCompare(b.patientData.birthday),
      render: value => <span className={tabularNums}>{dayjs(value).format(SHORT_DATE_FORMAT)}</span>,
    },
    {
      title: 'Probenentnahme am',
      dataIndex: 'sampledAt',
      key: 'sampledAt',
      sorter: (a, b) => (a.sampledAt ?? '').localeCompare(b.sampledAt ?? ''),
      width: 180,
      ellipsis: true,
      render: value => (value ? <span className={tabularNums}>{dayjs(value).format(SHORT_DATETIME_FORMAT)}</span> : ''),
    },
    {
      title: 'Auftrag',
      dataIndex: 'orders',
      key: 'orders',
      width: 100,
      ellipsis: true,
      render: (_, record) => (
        <Space>
          {record.ordersTruncated && (
            <Tooltip title="Multi-Labor-Anforderung: Sie haben keine Berechtigung für alle Aufträge dieser Anforderung.">
              <WarningTwoTone twoToneColor={token.colorWarning} />
            </Tooltip>
          )}
          {[...record.orders]
            .sort((a, b) => a.lab.id.localeCompare(b.lab.id))
            .map((order, index, orders) => (
              <span key={order.id}>
                <span className={mono}>
                  <Markable tokens={debouncedSearch ?? ''}>{order.number}</Markable>
                </span>{' '}
                <Typography.Text type="secondary">{order.lab.shortName}</Typography.Text>
                {orders.length - 1 > index ? ' - ' : ''}
              </span>
            ))}
        </Space>
      ),
    },
    {
      title: 'Erstellt von',
      key: 'creator',
      width: 110,
      ellipsis: true,
      sorter: (a, b) => a.createdByLastName.localeCompare(b.createdByLastName),
      render: (_, record) => `${record.createdByFirstName} ${record.createdByLastName}`,
    },
    {
      title: 'Bearbeitet von',
      key: 'modifier',
      width: 140,
      ellipsis: true,
      sorter: (a, b) => a.modifiedByLastName.localeCompare(b.modifiedByLastName),
      render: (_, record) => `${record.modifiedByFirstName} ${record.modifiedByLastName}`,
    },
    {
      title: 'Bearbeitet am',
      dataIndex: 'modifiedAt',
      key: 'modifiedAt',
      width: 130,
      ellipsis: true,
      sorter: (a, b) => {
        if (!a.modifiedAt && !b.modifiedAt) return 0;
        if (a.modifiedAt && !b.modifiedAt) return 1;
        if (!a.modifiedAt && b.modifiedAt) return 0;
        return a.modifiedAt!.localeCompare(b.modifiedAt!);
      },
      render: value => (value ? <span className={tabularNums}>{dayjs(value).format(SHORT_DATETIME_FORMAT)}</span> : null),
    },
    {
      title: 'Kommentar',
      dataIndex: 'comment',
      key: 'comment',
      width: 90,
      ellipsis: true,
      sorter: false,
      render: comment =>
        comment ? (
          <Popover trigger={['hover']} content={<div style={{ whiteSpace: 'pre-wrap', maxWidth: '500px' }}>{comment}</div>}>
            <Button
              type="link"
              className={css`
                height: 20px;
                margin: 0;
                padding: 0;
              `}
              icon={<EyeOutlined />}
            />
          </Popover>
        ) : null,
    },
  ];

  const actionsColumn: ColumnType<Request> = {
    title: '',
    key: 'actions',
    fixed: 'right',
    align: 'right',
    ellipsis: true,
    width: '100px',
    className: tableActionCell,
    render: (_, record) => {
      const items: ItemType[] = [];
      const truncated = isTruncated(record);
      items.push(printMenuItem('print', 'Drucken', canPrint, () => printOnce(record), printAvailable));
      items.push(...patientMenuItems(record.patientData));
      if (editAllowed) {
        items.push({
          label: 'Bearbeiten',
          key: 'edit',
          icon: <EditOutlined />,
          onClick: info => {
            info.domEvent.stopPropagation();
            onEdit(record.id);
          },
        });
      }
      if (!launchOnly && !record.aisLinked && (truncated || record.orders.filter(it => it.type === OrderType.INITIAL).length > 1)) {
        items.push({
          label: 'Aufteilen',
          key: 'split',
          icon: <SplitCellsOutlined />,
          onClick: info => {
            info.domEvent.stopPropagation();
            onSplit(record.id);
          },
        });
      }
      if (deletedAllowed) {
        items.push({
          label: 'Löschen',
          danger: true,
          key: 'delete',
          icon: <DeleteOutlined />,
          onClick: info => {
            info.domEvent.stopPropagation();

            if (truncated) {
              offerSplit(modal, launchOnly || record.aisLinked, 'Löschen', 'löschen', () => onSplit(record.id));
              return;
            }

            setDeleteRequest(record);
          },
        });
      }

      return (
        <>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={e => {
              e.stopPropagation();
              setDetailsRequestId(record.id);
            }}
          >
            Details
          </Button>
          <Dropdown menu={{ items: items }} trigger={['click']} placement="bottomRight">
            <Button
              icon={<EllipsisOutlined style={{ fontSize: '20px', verticalAlign: 'middle' }} />}
              type="text"
              onClick={e => e.stopPropagation()}
            />
          </Dropdown>
        </>
      );
    },
  };

  const filteredColumns: ColumnsType<Request> = [
    ...columnSettings
      .filter(cs => cs.visible)
      .map(cs => {
        return columns.find(c => c.key === cs.id)!;
      }),
    actionsColumn,
  ];

  useEffect(() => {
    const filtered = filterByTokens(data?.openRequests ?? [], debouncedSearch, [
      r => r.patientData.firstName,
      r => r.patientData.lastName,
      r => r.patientData.svnr,
      r => r.orders.map(order => order.number),
    ]);
    setFilteredRequests(filtered);
  }, [debouncedSearch, data]);

  const deleteSelectedRequests = async () => {
    try {
      await deleteOpenRequestsMutation({
        variables: {
          requestIds: selectedRequests.map(it => it.id),
        },
      });

      message.success('Ausgewählte Anforderungen wurden gelöscht');
    } catch {
      message.error('Anforderungen konnten nicht gelöscht werden');
    } finally {
      setSelectedRequests([]);
    }
  };

  const deleteOpenRequest = async (requestId: string | undefined | null) => {
    if (!requestId) {
      return;
    }

    try {
      await deleteOpenRequestsMutation({
        variables: {
          requestIds: [requestId],
        },
      });

      message.success('Anforderung wurde gelöscht');
    } catch {
      message.error('Anforderung konnte nicht gelöscht werden');
    } finally {
      setDeleteRequest(null);
      setSelectedRequests([...selectedRequests.filter(it => it.id !== requestId)]);
    }
  };

  const sendRequests = async (generateLocally: boolean) => {
    const emptyLabelsCount = data?.doctor?.addEmptyLabels ?? 0;
    try {
      let requests;
      if (generateLocally) {
        requests = await generateAndExport(selectedRequests.map(it => it.id));
      } else {
        const response = await send(
          selectedRequests.map(it => it.id),
          false
        );
        requests = response.data!.sendRequests!;
      }

      for (const request of requests) {
        const labelsPrinted = await print(
          'send',
          request,
          () => message.error('Beim Drucken ist ein Fehler aufgetreten'),
          () => {},
          () => {},
          () => {},
          () => {}
        );
        const patientData = selectedRequests.find(it => it.id === request.id)?.patientData;
        if (labelsPrinted && emptyLabelsCount && patientData) {
          printEmptyLabel(
            {
              doctorId: currentDoctorId,
              patientTitle: patientData.title,
              patientLastName: patientData.lastName,
              patientFirstName: patientData.firstName,
              patientSvnr: patientData.svnr,
              patientBirthday: patientData.birthday,
              insuranceCode: patientData.insuranceCode,
            },
            emptyLabelsCount,
            () => message.error(`Beim Drucken der Leeretiketten für ${patientFullName(patientData)} ist ein Fehler aufgetreten`),
            () => {}, // silently ignore
            request.id
          );
        }
        await aisExport(
          request,
          [],
          msg => message.error(`Beim AIS-Export ist ein Fehler aufgetreten: ${msg}`),
          () => {}
        );
      }
      message.success('Anforderungen wurden gesendet');
    } catch {
      message.error('Anforderungen konnten nicht gesendet werden');
    } finally {
      setSelectedRequests([]);
    }
  };

  const sendRequest = async (requestId: string, generateLocally: boolean) => {
    try {
      let request;
      if (generateLocally) {
        const requests = await generateAndExport([requestId]);
        request = requests[0];
      } else {
        const response = await send([requestId], false);
        request = response.data!.sendRequests![0];
      }

      await print(
        'send',
        request,
        () => message.error('Beim Drucken ist ein Fehler aufgetreten'),
        () => {},
        () => {},
        () => {},
        () => {}
      );
      await aisExport(
        request,
        [],
        () => message.error('Beim AIS-Export ist ein Fehler aufgetreten'),
        () => {}
      );
      message.success('Anforderung wurde gesendet');
    } catch {
      message.error('Anforderung konnte nicht gesendet werden');
    } finally {
      setSelectedRequests(selectedRequests.filter(it => it.id !== requestId));
    }
  };

  const moreActionItems: ItemType[] = [
    // a CSV import creates Anforderungen without the AIS
    ...itemsIf(!launchOnly, {
      key: 'csv-import',
      icon: data?.doctor?.csvImportEnabled ? <CloudUploadOutlined /> : <LockOutlined />,
      label: 'CSV-Import',
      onClick: () =>
        data?.doctor?.csvImportEnabled
          ? navigate('/offene-anforderungen/csv-import')
          : modal.warning({
              icon: <LockOutlined />,
              title: 'CSV-Import',
              width: 500,
              content: (
                <>
                  <p>
                    Mit dem CSV-Import können Patienten- und Anforderungsdaten mittels CSV-Datei hochgeladen und automatisch zu
                    Anforderungen verarbeitet werden. Dies ist z.B. hilfreich für Massenverarbeitungen.
                  </p>
                  <p>Bitte kontaktieren Sie Ihr Labor bei Interesse an dieser Funktion.</p>
                </>
              ),
            }),
    }),
    ...itemsIf(localExportAllowed, {
      key: 'local-export',
      icon: <FileZipOutlined />,
      disabled: selectedRequests.length <= 0 || !window.nativeApi,
      label: selectedRequests.length > 0 ? `Dateien lokal erzeugen (${selectedRequests.length})` : 'Dateien lokal erzeugen',
      onClick: () => sendRequests(true),
    }),
    ...itemsIf(
      deletedAllowed,
      { type: 'divider' as const, key: 'delete-divider' },
      {
        key: 'delete-selected',
        danger: true,
        icon: <DeleteOutlined />,
        disabled: selectedRequests.length <= 0,
        label: selectedRequests.length > 0 ? `Ausgewählte löschen (${selectedRequests.length})` : 'Ausgewählte löschen',
        onClick: () =>
          modal.confirm({
            title: 'Ausgewählte Anforderungen löschen',
            icon: <WarningTwoTone twoToneColor="#f5222d" /* red-6 */ />,
            content:
              selectedRequests.length === 1
                ? 'Möchten Sie die ausgewählte Anforderung wirklich löschen?'
                : `Möchten Sie die ${selectedRequests.length} ausgewählten Anforderungen wirklich löschen?`,
            okText: 'Löschen',
            okButtonProps: { danger: true, icon: <DeleteOutlined /> },
            cancelText: 'Abbrechen',
            onOk: deleteSelectedRequests,
          }),
      }
    ),
  ];

  return (
    <MainContent>
      <PageHeader
        title="Offene Anforderungen"
        style={{ padding: 0, paddingBottom: 'inherit' }}
        extra={
          <Flex wrap gap={token.paddingXS}>
            <Input
              autoFocus
              allowClear
              placeholder="Suche"
              value={search ?? ''}
              onChange={e => setSearch(e.target.value)}
              prefix={<SearchOutlined />}
              suffix={
                <Tooltip title="Suche nach Vorname, Nachname, Auftragsnummer oder SVNR">
                  <InfoCircleOutlined />
                </Tooltip>
              }
              className={css`
                width: 200px;
                @media (min-width: 1450px) {
                  width: 300px;
                }
              `}
            />
            <Badge
              count={selectedRequests.length}
              offset={[-5, 0]}
              className={css`
                .ant-badge-count {
                  background: ${token.colorPrimary};
                  box-shadow: 0 0 0 2px ${token.colorBgContainer};
                }
              `}
            >
              <Button type="primary" icon={<SendOutlined />} disabled={selectedRequests.length <= 0} onClick={() => sendRequests(false)}>
                Anforderungen senden
              </Button>
            </Badge>
            <Dropdown menu={{ items: moreActionItems }} trigger={['click']} placement="bottomRight">
              <Button>
                Weitere Aktionen <DownOutlined />
              </Button>
            </Dropdown>
            <TableColumnSettings tableKey="openRequests" initialColumnSettings={columnSettings} onChange={setColumnSettings} />
          </Flex>
        }
        className={css`
          .ant-page-header-heading {
            flex-wrap: wrap;
          }
        `}
      />
      <Table<Request>
        scroll={{ x: 'max-content' }}
        sticky
        showSorterTooltip={false}
        rowKey={record => record.id}
        size="middle"
        dataSource={filteredRequests}
        pagination={{
          size: 'middle',
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]} bis ${range[1]} von ${total} offenen Anforderungen`,
        }}
        loading={loading}
        columns={filteredColumns}
        rowSelection={{
          columnWidth: 24,
          type: 'checkbox',
          selectedRowKeys: selectedRequests.map(it => it.id),
          onChange: (_, selectedRows) => setSelectedRequests(selectedRows),
          getCheckboxProps: record => ({
            disabled: isDefineLater(record) || isTruncated(record),
          }),
        }}
        onRow={record => ({
          onClick: () => {
            if (selectedRequests.map(it => it.id).includes(record.id)) {
              setSelectedRequests(selectedRequests.filter(it => it.id !== record.id));
              return;
            }

            if (isTruncated(record)) {
              offerSplit(modal, launchOnly || record.aisLinked, 'Senden', 'senden', () => onSplit(record.id));
              return;
            }

            if (isDefineLater(record)) {
              if (!editAllowed) {
                modal.info({
                  title: 'Senden nicht möglich',
                  content:
                    'Es wurden noch nicht alle benötigen Verrechnungsinformationen angegeben. Sie haben jedoch keine Berechtigung zum Bearbeiten von Anforderungen.',
                });
                return;
              }

              modal.confirm({
                maskClosable: true,
                title: 'Senden nicht möglich',
                content:
                  'Es wurden noch nicht alle benötigen Verrechnungsinformationen angegeben. Wollen Sie die Anforderung jetzt bearbeiten?',
                okText: 'Anforderung bearbeiten',
                cancelText: 'Abbrechen',
                okButtonProps: { icon: <EditOutlined /> },
                onOk: () => onEdit(record.id),
              });
              return;
            }

            setSelectedRequests([...selectedRequests, record]);
          },
        })}
        rowClassName={tableHoverPointer}
      />
      <Modal
        title={
          <>
            <WarningTwoTone twoToneColor="#f5222d" /* red-6 */ /> Anforderung löschen
          </>
        }
        open={!!deleteRequest}
        okButtonProps={{ danger: true, icon: <DeleteOutlined /> }}
        okText="Anforderung löschen"
        onOk={() => deleteOpenRequest(deleteRequest?.id)}
        onCancel={() => setDeleteRequest(null)}
      >
        Wollen Sie die ausgewählte offene Anforderung wirklich löschen?
      </Modal>
      <RequestDetailsModal requestId={detailsRequestId} onClose={() => setDetailsRequestId(null)} />
      <FormsModal patientData={formsPatientData} onClose={() => setFormsPatientData(null)} />
    </MainContent>
  );
};
